import { useState, useRef, useEffect } from "react";
import { Input } from "@base-ui/react/input";
import { Dialog } from "./Dialog";
import { Button } from "./Button";
import {
    type ProviderId,
    PROVIDER_KEY_PLACEHOLDERS,
} from "../lib/model-catalog";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onUpload: (files: File[], apiKey: string) => Promise<void>;
    onSaveApiKey?: (apiKey: string) => Promise<void>;
    hasStoredKey: boolean;
    provider: ProviderId;
    providerLabel: string;
    pendingFiles: File[];
    configureKeyOnly?: boolean;
}

export function UploadModal({
    isOpen,
    onClose,
    onUpload,
    onSaveApiKey,
    hasStoredKey,
    provider,
    providerLabel,
    pendingFiles,
    configureKeyOnly,
}: Props) {
    const [apiKey, setApiKey] = useState("");
    const [files, setFiles] = useState<File[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const activeFiles = pendingFiles.length > 0 ? pendingFiles : files;
    const needsApiKey = !hasStoredKey && !apiKey.trim();
    const showFileUpload = pendingFiles.length === 0 && !configureKeyOnly;

    useEffect(() => {
        if (!isOpen) {
            setFiles([]);
            setApiKey("");
            setError(null);
        }
    }, [isOpen]);

    function handleDragOver(e: React.DragEvent) {
        e.preventDefault();
        setIsDragging(true);
    }

    function handleDragLeave(e: React.DragEvent) {
        e.preventDefault();
        setIsDragging(false);
    }

    function handleDrop(e: React.DragEvent) {
        e.preventDefault();
        setIsDragging(false);
        setError(null);

        const droppedFiles = Array.from(e.dataTransfer.files).filter(
            (f) => f.type === "application/pdf",
        );
        if (droppedFiles.length > 0) {
            setFiles((prev) => [...prev, ...droppedFiles]);
        } else {
            setError("Please upload PDF files");
        }
    }

    function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
        setError(null);
        const selectedFiles = Array.from(e.target.files || []).filter(
            (f) => f.type === "application/pdf",
        );
        if (selectedFiles.length > 0) {
            setFiles((prev) => [...prev, ...selectedFiles]);
        } else if (e.target.files?.length) {
            setError("Please upload PDF files");
        }
    }

    async function handleSubmit() {
        if (configureKeyOnly) {
            if (!apiKey.trim()) {
                setError(`Please enter your ${providerLabel} API key`);
                return;
            }
            setIsLoading(true);
            setError(null);
            try {
                await onSaveApiKey?.(apiKey.trim());
                setApiKey("");
                onClose();
            } catch (err) {
                setError(
                    err instanceof Error
                        ? err.message
                        : "Failed to save API key",
                );
            } finally {
                setIsLoading(false);
            }
            return;
        }

        if (needsApiKey) {
            setError(`Please enter your ${providerLabel} API key`);
            return;
        }
        if (activeFiles.length === 0) {
            setError("Please select at least one PDF file");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await onUpload(activeFiles, apiKey.trim());
            setFiles([]);
            setApiKey("");
            onClose();
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "Failed to process PDF",
            );
        } finally {
            setIsLoading(false);
        }
    }

    function handleClose() {
        if (!isLoading) {
            setFiles([]);
            setApiKey("");
            setError(null);
            onClose();
        }
    }

    const title = configureKeyOnly
        ? `${providerLabel} API Key`
        : pendingFiles.length > 0
          ? `Enter ${providerLabel} API Key`
          : "Upload Tax Return";

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            title={title}
            closeDisabled={isLoading}
        >
            {/* Pending files indicator */}
            {pendingFiles.length > 0 && (
                <div className="mb-4 text-sm">
                    <div className="text-(--color-text-muted)">
                        {pendingFiles.length} file
                        {pendingFiles.length > 1 ? "s" : ""} selected
                    </div>
                    <div className="text-xs text-(--color-text-muted) mt-1">
                        {pendingFiles.map((f, i) => (
                            <div key={i} className="truncate">
                                {f.name}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* API Key input - only show if no stored key and not just configuring */}
            {!hasStoredKey && !configureKeyOnly && (
                <div className="mb-4">
                    <label className="block text-xs text-(--color-text-muted) mb-1.5">
                        {providerLabel} API Key
                    </label>
                    <Input
                        type="password"
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        placeholder={PROVIDER_KEY_PLACEHOLDERS[provider]}
                        disabled={isLoading}
                        autoComplete="off"
                        data-1p-ignore
                        data-lpignore="true"
                        className="w-full px-3 py-2 border border-(--color-border) bg-(--color-bg-muted) rounded-lg text-sm placeholder:text-(--color-text-muted) focus:outline-none focus:border-(--color-text-muted) disabled:opacity-50"
                    />
                    <p className="text-xs text-(--color-text-muted) mt-1.5">
                        Saved locally to .env file
                    </p>
                </div>
            )}

            {/* File upload area */}
            {showFileUpload && (
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() =>
                        !isLoading && fileInputRef.current?.click()
                    }
                    className={[
                        "border border-dashed rounded-xl p-6 text-center cursor-pointer text-sm transition-colors",
                        isDragging
                            ? "border-(--color-text-muted) bg-(--color-bg-muted)"
                            : "border-(--color-border) hover:border-(--color-text-muted)",
                        isLoading ? "opacity-50 cursor-not-allowed" : "",
                    ].join(" ")}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf"
                        multiple
                        onChange={handleFileSelect}
                        disabled={isLoading}
                        className="hidden"
                    />
                    {files.length > 0 ? (
                        <div>
                            <div>
                                {files.length} file
                                {files.length > 1 ? "s" : ""} selected
                            </div>
                            <div className="text-xs text-(--color-text-muted) mt-1">
                                {files.map((f, i) => (
                                    <div key={i} className="truncate">
                                        {f.name}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="text-(--color-text-muted)">
                            Drop PDF files here or click to browse
                        </div>
                    )}
                </div>
            )}

            {/* Error message */}
            {error && (
                <div className="mt-4 text-sm text-(--color-negative)">
                    {error}
                </div>
            )}

            {/* Privacy note */}
            {!configureKeyOnly && (
                <div className="mt-4 text-xs text-(--color-text-muted)">
                    Your tax return is sent directly to the selected AI provider. Data
                    stored locally.
                </div>
            )}

            {/* Submit button */}
            <Button
                onClick={handleSubmit}
                disabled={
                    isLoading ||
                    (configureKeyOnly
                        ? !apiKey.trim()
                        : needsApiKey || activeFiles.length === 0)
                }
                className="mt-4 w-full"
            >
                {isLoading
                    ? configureKeyOnly
                        ? "Saving..."
                        : "Processing..."
                    : configureKeyOnly
                      ? "Save API key"
                      : `Parse ${activeFiles.length > 1 ? `${activeFiles.length} returns` : "tax return"}`}
            </Button>
        </Dialog>
    );
}
